import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Megaphone, UsersThree, Plus, PaperPlaneTilt, ChartBar } from "@phosphor-icons/react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const SEG_TYPES = [["active", "Active (recent)"], ["dormant", "Dormant"], ["new", "New"], ["near_stamp", "Near a stamp reward"], ["all", "All customers"]];

export default function Engagement() {
  const [segments, setSegments] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [seg, setSeg] = useState({ name: "", type: "dormant", days: 30 });
  const [camp, setCamp] = useState({ name: "", segment_id: "", offer: "Bonus points", holdout_percent: 10, channels: ["in_app"] });
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [s, c] = await Promise.all([api.get("/business/segments"), api.get("/business/campaigns")]);
      setSegments(s.data); setCampaigns(c.data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const createSeg = async () => {
    if (!seg.name.trim()) return toast.error("Name the segment");
    try { await api.post("/business/segments", seg); toast.success("Segment created"); setSeg({ ...seg, name: "" }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const previewSeg = async (id) => {
    try { const { data } = await api.get(`/business/segments/${id}/preview`); toast.success(`${data.count} customer(s) match`); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const createCamp = async () => {
    if (!camp.name.trim() || !camp.segment_id) return toast.error("Name it and pick a segment");
    try { await api.post("/business/campaigns", camp); toast.success("Campaign created (draft)"); setCamp({ ...camp, name: "" }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const launch = async (id) => {
    try { const { data } = await api.post(`/business/campaigns/${id}/launch`); toast.success(`Launched — ${data.sent} sent, ${data.control} held out`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const showResults = async (id) => {
    try { const { data } = await api.get(`/business/campaigns/${id}/results`); setResults({ ...results, [id]: data }); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="engagement-page">
      <div>
        <h1 className="font-display font-bold text-2xl sm:text-3xl flex items-center gap-2"><Megaphone size={26} weight="duotone" className="text-primary" /> Engagement</h1>
        <p className="text-muted-foreground text-sm">Group your customers and reach them with measured campaigns.</p>
      </div>
      {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {!loading && <>
      <div className="card-surface p-4 sm:p-5">
        <p className="font-display font-semibold mb-3 flex items-center gap-2"><UsersThree size={18} className="text-primary" /> Segments</p>
        <div className="flex flex-col sm:flex-row gap-2 mb-4">
          <Input data-testid="segment-name" value={seg.name} onChange={(e) => setSeg({ ...seg, name: e.target.value })} placeholder="e.g. Inactive 30 days" className="bg-muted/40 border-black/10 h-11 rounded-xl" />
          <Select value={seg.type} onValueChange={(v) => setSeg({ ...seg, type: v })}><SelectTrigger data-testid="segment-type" className="h-11 rounded-xl sm:w-52 bg-muted/40 border-black/10"><SelectValue /></SelectTrigger>
            <SelectContent>{SEG_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select>
          <Button data-testid="create-segment" onClick={createSeg} className="h-11 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-semibold shrink-0"><Plus size={16} className="mr-1" /> Add</Button>
        </div>
        <div className="divide-y divide-black/5">
          {segments.map((s) => (
            <div key={s.id} data-testid={`segment-${s.id}`} className="flex items-center justify-between py-2.5">
              <p className="text-sm font-medium truncate">{s.name} <span className="text-xs text-muted-foreground">· {s.type}</span></p>
              <button data-testid={`preview-${s.id}`} onClick={() => previewSeg(s.id)} className="text-xs font-semibold text-primary hover:bg-primary/10 rounded-full px-3 py-1.5">Preview count</button>
            </div>
          ))}
          {segments.length === 0 && <p className="text-sm text-muted-foreground py-2">No segments yet.</p>}
        </div>
      </div>

      <div className="card-surface p-4 sm:p-5">
        <p className="font-display font-semibold mb-3 flex items-center gap-2"><PaperPlaneTilt size={18} className="text-primary" /> Campaigns</p>
        <div className="grid sm:grid-cols-2 gap-2 mb-2">
          <Input data-testid="campaign-name" value={camp.name} onChange={(e) => setCamp({ ...camp, name: e.target.value })} placeholder="e.g. Come Back This Weekend" className="bg-muted/40 border-black/10 h-11 rounded-xl" />
          <Select value={camp.segment_id} onValueChange={(v) => setCamp({ ...camp, segment_id: v })}><SelectTrigger data-testid="campaign-segment" className="h-11 rounded-xl bg-muted/40 border-black/10"><SelectValue placeholder="Target segment" /></SelectTrigger>
            <SelectContent>{segments.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select>
          <Input data-testid="campaign-offer" value={camp.offer} onChange={(e) => setCamp({ ...camp, offer: e.target.value })} placeholder="Offer" className="bg-muted/40 border-black/10 h-11 rounded-xl" />
          <div><Label className="text-[11px] text-muted-foreground">Holdout %</Label><Input data-testid="campaign-holdout" type="number" value={camp.holdout_percent} onChange={(e) => setCamp({ ...camp, holdout_percent: parseInt(e.target.value) || 0 })} className="h-10 rounded-lg bg-muted/40 border-black/10 mt-0.5" /></div>
        </div>
        <Button data-testid="create-campaign" onClick={createCamp} className="rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-semibold mb-4"><Plus size={16} className="mr-1" /> Create campaign</Button>
        <div className="divide-y divide-black/5">
          {campaigns.map((c) => (
            <div key={c.id} data-testid={`campaign-${c.id}`} className="py-2.5">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium truncate">{c.name} <span className="text-xs text-muted-foreground">· {c.segment_name} · {c.status}</span></p>
                <div className="flex gap-1.5 shrink-0">
                  {c.status !== "active"
                    ? <button data-testid={`launch-${c.id}`} onClick={() => launch(c.id)} className="text-xs font-semibold text-primary hover:bg-primary/10 rounded-full px-3 py-1.5">Launch</button>
                    : <button data-testid={`results-${c.id}`} onClick={() => showResults(c.id)} className="text-xs font-semibold text-muted-foreground hover:bg-muted rounded-full px-3 py-1.5 flex items-center gap-1"><ChartBar size={13} /> Results</button>}
                </div>
              </div>
              {results[c.id] && (
                <div data-testid={`campaign-results-${c.id}`} className="mt-2 text-xs bg-primary/10 rounded-xl p-3">
                  Treatment {results[c.id].treatment.rate}% ({results[c.id].treatment.converted}/{results[c.id].treatment.size}) vs Control {results[c.id].control.rate}% — <span className="font-semibold text-primary">incremental {results[c.id].incremental_rate}%</span>
                </div>
              )}
            </div>
          ))}
          {campaigns.length === 0 && <p className="text-sm text-muted-foreground py-2">No campaigns yet.</p>}
        </div>
      </div>
      </>}
    </div>
  );
}
